import React, { useEffect, useState } from 'react';
import { Box, Card, Divider, Grid, SwipeableDrawer, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { closePOSDrawer, getPOSDrawerDetails, hidePOSCloseTab, viewPOSCloseTab } from '../../../redux/slices/posRedux/posRedux';
import { useSelector, dispatch } from '../../../redux/store';
import { PieChart } from '@mui/x-charts/PieChart';
import { getPaymentTypes } from '../../../redux/slices/financeRedux/paymentTypeRedux';
import { fCurrency } from '../../../utils/formatNumber';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';

const EXCLUDED_PAYMENT_SUMMARY_STATUSES = ['Refunded', 'Canceled'];

const isPaymentSummaryOrder = (posData) => !EXCLUDED_PAYMENT_SUMMARY_STATUSES.includes(posData?.status);

export default function POSClose() {
  let paymentTypes;
  ({ data: paymentTypes } = useSelector((state) => state.paymentTypes));
  const { viewPosCloseStatus, posDrawerData } = useSelector((state) => state?.pos);
  const { loggedInUser } = useSelector((state) => state?.user);
  const [paymentDataForPieChart, setPaymentDataForPieChart] = useState([]);
  const [posDataList, setPosDataList] = useState([]);
  const { selectedLocation } = useSelector((state) => state?.location);

  useEffect(() => {
    if (viewPosCloseStatus) {
      dispatch(getPOSDrawerDetails(loggedInUser?.posCashDrawerID));
      dispatch(getPaymentTypes());
    }
  }, [viewPosCloseStatus, loggedInUser?.posCashDrawerID, dispatch]);

  useEffect(() => {
    if (posDrawerData?.posData?.length > 0) {
      setPosDataList({
        ...posDrawerData,
        posData: [].concat(...posDrawerData?.posData, [
          {
            id: 'Starting Amount',
            status: 'Opening',
            posPayments: [
              {
                amount: posDrawerData?.startingAmount,
                paymentTypesId: 1,
                id: 1,
                posId: 0,
              },
            ],
          },
        ]),
      });
    } else {
      setPosDataList([]);
    }
  }, [posDrawerData]);

  const sumPosPaymentsByType = (posData) => {
    const sumByType = {};
    posData?.forEach((data) => {
      if (isPaymentSummaryOrder(data)) {
        data?.posPayments?.forEach((payment) => {
          const typeId = payment.paymentTypesId;
          if (sumByType[typeId]) {
            sumByType[typeId] += payment.amount;
          } else {
            sumByType[typeId] = payment.amount;
          }
        });
      }
    });
    return sumByType;
  };

  const getPosDataBasedOnPaymentType = (posData) => {
    const posDataBasedOnPaymentType = {};

    posData?.forEach((data) => {
      if (!isPaymentSummaryOrder(data)) {
        return;
      }

      data?.posPayments?.forEach((payment) => {
        const typeId = payment.paymentTypesId;
        if (!posDataBasedOnPaymentType[typeId]) {
          posDataBasedOnPaymentType[typeId] = {
            totalAmount: 0,
            details: [],
          };
        }
        posDataBasedOnPaymentType[typeId].totalAmount += payment.amount;
        posDataBasedOnPaymentType[typeId].details.push({
          posId: data?.id,
          posStatus: data?.status,
          amount: payment.amount,
          verifierId: payment?.verifierId || '-',
          paymentTypeId: payment?.paymentTypesId,
        });
      });
    });
    return posDataBasedOnPaymentType;
  };

  const paymentTypeBreakdown = getPosDataBasedOnPaymentType(posDataList?.posData);
  const sumByType = sumPosPaymentsByType(posDataList?.posData);

  const paymentSummary =
    paymentTypes
      ?.map((paymentType) => {
        const paymentTypeData = paymentTypeBreakdown?.[paymentType?.id];
        return {
          id: paymentType?.id,
          name: paymentType?.name,
          totalAmount: paymentTypeData?.totalAmount || 0,
          transactionCount: paymentTypeData?.details?.length || 0,
          details: paymentTypeData?.details || [],
          includesOpeningAmount: paymentTypeData?.details?.some((detail) => detail?.posStatus === 'Opening'),
        };
      })
      ?.filter((paymentType) => paymentType?.totalAmount > 0 && sumByType[paymentType?.id]) || [];

  const totalAmount = paymentSummary?.reduce((sum, paymentType) => sum + paymentType.totalAmount, 0);
  useEffect(() => {
    const sumByType = sumPosPaymentsByType(posDataList?.posData);
    setPaymentDataForPieChart(
      paymentTypes
        ?.map((paymentType) => {
          return {
            id: paymentType?.id,
            value: sumByType[paymentType?.id] || 0,
            label: paymentType?.name,
          };
        })
        ?.filter((paymentType) => sumByType[paymentType?.id])
    );
  }, [paymentTypes, posDataList]);

  return (
    <SwipeableDrawer
      anchor={'bottom'}
      open={viewPosCloseStatus}
      onOpen={() => {
        dispatch(viewPOSCloseTab());
      }}
      onClose={() => {
        dispatch(hidePOSCloseTab());
      }}
      PaperProps={{
        sx: {
          width: '98%',
          height: '98%',
          marginLeft: '1%',
          marginTop: '1%',
          borderRadius: '10px',
        },
      }}
    >
      <Grid container spacing={3} p={3}>
        <Grid item xs={12} md={12}>
          <Box display='flex' justifyContent='space-between' alignItems='center'>
            <Box>
              <Typography variant='h4'>Registry Status</Typography>
              <Typography variant='body2' color='text.secondary'>
                Review the payment distribution and summary before closing the POS.
              </Typography>
            </Box>
            <Box sx={{ fontSize: 30, ml: 2, cursor: 'pointer' }}>
              <a
                onClick={() => {
                  dispatch(hidePOSCloseTab());
                }}
              >
                x
              </a>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, height: '100%' }}>
            <Card sx={{ p: 3, borderRadius: 3, background: 'linear-gradient(135deg, rgba(25,118,210,0.08), rgba(25,118,210,0.02))' }}>
              <Typography variant='overline' color='text.secondary'>
                Total Collected
              </Typography>
              <Typography variant='h3' sx={{ mt: 1 }}>
                {fCurrency(totalAmount || 0)}
              </Typography>
              <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
                Combined total across all recorded payment types.
              </Typography>
            </Card>

            <Card sx={{ p: 3, flexGrow: 1, minHeight: 420, borderRadius: 3 }}>
              <Typography variant='h6'>Payment Chart</Typography>
              <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                The pie chart shows how the register total is split across payment methods.
              </Typography>

              {paymentDataForPieChart?.length > 0 ? (
                <PieChart
                  series={[
                    {
                      data: paymentDataForPieChart,
                      innerRadius: 55,
                      outerRadius: 110,
                      paddingAngle: 2,
                      cornerRadius: 4,
                      highlightScope: { faded: 'global', highlighted: 'item' },
                      faded: { innerRadius: 45, additionalRadius: -12 },
                    },
                  ]}
                  height={320}
                />
              ) : (
                <Box
                  sx={{
                    minHeight: 320,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px dashed',
                    borderColor: 'divider',
                    borderRadius: 2,
                    bgcolor: 'grey.50',
                  }}
                >
                  <Typography variant='body2' color='text.secondary'>
                    No payment data is available for this register yet.
                  </Typography>
                </Box>
              )}
            </Card>
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ p: 3, height: '100%', minHeight: 420, maxHeight: '70vh', overflowY: 'auto', borderRadius: 3 }}>
            <Typography variant='h6' sx={{ mb: 2.5 }}>
              Payment Details
            </Typography>

            {paymentSummary?.length > 0 ? (
              <Box>
                {paymentSummary?.map((paymentType) => (
                  <Box
                    key={paymentType?.id}
                    sx={{
                      mb: 2,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      overflow: 'hidden',
                      bgcolor: 'background.paper',
                    }}
                  >
                    <Box
                      sx={{
                        px: 2,
                        py: 1.5,
                        bgcolor: 'grey.100',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        gap: 2,
                      }}
                    >
                      <Box>
                        <Typography variant='subtitle1'>{paymentType?.name}</Typography>
                        <Typography variant='body2' color='text.secondary'>
                          {paymentType?.transactionCount} entr{paymentType?.transactionCount === 1 ? 'y' : 'ies'}
                          {paymentType?.includesOpeningAmount ? ' • includes opening amount' : ''}
                        </Typography>
                      </Box>
                      <Typography variant='subtitle1' sx={{ whiteSpace: 'nowrap', color: 'primary.main' }}>
                        {fCurrency(paymentType?.totalAmount)}
                      </Typography>
                    </Box>
                    <TableContainer>
                      <Table size='small'>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 700 }}>Order Number</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Verifier ID</TableCell>
                            <TableCell sx={{ fontWeight: 700 }} align='right'>
                              Amount
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {paymentType?.details?.map((detail, index) => (
                            <TableRow key={`${paymentType?.id}-${detail?.posId}-${index}`} hover>
                              <TableCell>{detail?.posId}</TableCell>
                              <TableCell>{detail?.posStatus}</TableCell>
                              <TableCell>{detail?.verifierId}</TableCell>
                              <TableCell align='right'>{fCurrency(detail?.amount)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                ))}
                <Divider sx={{ my: 0.5 }} />
                <Box display='flex' justifyContent='space-between' alignItems='center'>
                  <Typography variant='subtitle1'>Grand Total</Typography>
                  <Typography variant='h6'>{fCurrency(totalAmount || 0)}</Typography>
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  minHeight: 320,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px dashed',
                  borderColor: 'divider',
                  borderRadius: 2,
                  bgcolor: 'grey.50',
                }}
              >
                <Typography variant='body2' color='text.secondary'>
                  Payment summary will appear here once register payments are available.
                </Typography>
              </Box>
            )}
          </Card>
        </Grid>

        <Grid item xs={12} md={6} mx={'auto'}>
          <LoadingButton
            type='submit'
            variant='contained'
            size='large'
            style={{ width: '100%' }}
            onClick={() => {
              if (
                posDataList?.posData?.filter((value) => {
                  return value?.status === 'Preparing';
                }).length > 0
              ) {
                toast.error('Preparing orders are available. Please complete them before closing the register.');
              } else {
                if (confirm('Are you sure you want to close the register?')) {
                  dispatch(hidePOSCloseTab());
                  dispatch(
                    closePOSDrawer({
                      drawerId: posDrawerData?.posCashDrawersId,
                      locationId: selectedLocation?.id,
                      locationName: selectedLocation?.name,
                    })
                  );
                }
              }
            }}
          >
            Close Register
          </LoadingButton>
        </Grid>
        {/* <Grid item xs={12} md={4}>
          {' '}
        </Grid> */}
      </Grid>
    </SwipeableDrawer>
  );
}
